import React, { useState } from 'react';
import {
    Drawer,
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Button,
    Avatar,
    Divider,
    Chip,
    CircularProgress,
    Collapse,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppTheme } from '../App';
import { PRIMARY_ORANGE } from '../common/colors';
import { useRecentChats } from '../hooks/useRecentChats';
import { VisaChatSession } from '../hooks/useVisaChat';
import StarIcon from '@mui/icons-material/Star';

interface GlobalDrawerProps {
    open: boolean;
    onClose: () => void;
    variant?: 'temporary' | 'persistent';
    isMobile?: boolean;
}

const GlobalDrawer: React.FC<GlobalDrawerProps> = ({ open, onClose, variant = 'temporary', isMobile = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, currentUser, userData } = useAuth();
    const { isDarkMode, getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();
    const { recentChats, isLoading: isLoadingChats } = useRecentChats(5);

    // Function to get user initials
    const getUserInitials = () => {
        if (userData?.fullName) {
            const names = userData.fullName.trim().split(' ');
            if (names.length >= 2) {
                return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
            } else if (names.length === 1) {
                return names[0][0].toUpperCase();
            }
        }
        // Fallback to email first letter if no full name
        return currentUser?.email?.[0]?.toUpperCase() || 'U';
    };

    const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
        'global-talent-visa': false,
        'tourist-visa': false,
    });

    const handleSectionToggle = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const navigationItems = [
        { text: 'Home', icon: <HomeIcon />, path: '/', badge: undefined },
        { text: 'Subscription', icon: <StarIcon />, path: '/subscription', badge: undefined },
        { text: 'Profile', icon: <PersonIcon />, path: '/profile', badge: undefined },
    ];

    const visaSections = [
        {
            key: 'global-talent-visa',
            title: 'Global Talent Visa',
            icon: <WorkIcon />,
            items: [
                { 
                    text: 'Advanced Assistant', 
                    icon: <ChatIcon />, 
                    path: '/visa/global-talent-visa',
                    badge: undefined
                },
                { 
                    text: 'Eligibility Checker', 
                    icon: <AssessmentIcon />, 
                    path: '/visa/gtv-eligibility-checker',
                    badge: undefined
                },
                { 
                    text: 'Personal Statement Generator', 
                    icon: <EditIcon />, 
                    path: '/visa/personal-statement',
                    badge: undefined
                },
            ]
        },
        {
            key: 'tourist-visa',
            title: 'Tourist Visa',
            icon: <FlightTakeoffIcon />,
            items: [
                { 
                    text: 'Tourist Visa Assistant', 
                    icon: <ChatIcon />, 
                    path: '/visa/tourist-visa',
                    badge: 'BETA'
                },
            ]
        }
    ];

    const handleNavigation = (path: string) => {
        navigate(path);
        if (isMobile) {
        onClose();
        }
    };

    const handleLogout = () => {
        logout();
        if (isMobile) {
        onClose();
        }
    };

    const handleChatNavigation = (chatSession: VisaChatSession) => {
        const visaPath = chatSession.visaType === 'global-talent-visa'
            ? '/visa/global-talent-visa'
            : '/visa/tourist-visa';
        navigate(`${visaPath}/chat/${chatSession.id}`);
        if (isMobile) {
        onClose();
        }
    };

    const formatChatTime = (timestamp: string) => {
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

            if (diffInHours < 1) {
                return 'Just now';
            } else if (diffInHours < 24) {
                return `${Math.floor(diffInHours)}h ago`;
            } else {
                const diffInDays = Math.floor(diffInHours / 24);
                return `${diffInDays}d ago`;
            }
        } catch {
            return 'Recently';
        }
    };

    const getVisaIcon = (visaType: string) => {
        return visaType === 'global-talent-visa' ? <WorkIcon /> : <FlightTakeoffIcon />;
    };

    const getVisaDisplayName = (visaType: string) => {
        return visaType === 'global-talent-visa' ? 'Global Talent' : 'Tourist';
    };

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            variant={variant}
            sx={{
                '& .MuiDrawer-paper': {
                    width: 280,
                    boxSizing: 'border-box',
                    background: isDarkMode
                        ? 'rgba(26, 26, 26, 0.95)'
                        : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(16px)',
                    borderRight: `1px solid ${theme.borderColor}`,
                    boxShadow: isMobile 
                        ? (isDarkMode
                        ? '4px 0 20px rgba(0, 0, 0, 0.3)'
                            : '4px 0 20px rgba(0, 0, 0, 0.1)')
                        : 'none',
                    position: variant === 'persistent' ? 'fixed' : 'relative',
                },
                zIndex: theme => theme.zIndex.drawer + 2,
            }}
        >
            <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* User Profile Section */}
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        background: isDarkMode ? '#1e293b' : '#f8fafc',
                        borderBottom: `1px solid ${theme.borderColor}`,
                    }}
                >
                    <Avatar
                        sx={{
                            width: 60,
                            height: 60,
                            mx: 'auto',
                            mb: 2,
                            background: PRIMARY_ORANGE.main,
                            color: '#ffffff',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            boxShadow: isDarkMode 
                                ? '0 4px 16px rgba(0, 0, 0, 0.3)' 
                                : '0 4px 16px rgba(0, 0, 0, 0.1)',
                        }}
                        title={userData?.fullName || currentUser?.email || 'User'}
                    >
                        {getUserInitials()}
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: theme.textPrimary }}>
                        Welcome to thevisa.ai
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.textSecondary }}>
                        {userData?.fullName || currentUser?.email}
                    </Typography>
                </Box>

                {/* Navigation Section */}
                <Box sx={{ flex: 1, p: 2 }}>
                    <List sx={{ pt: 1 }}>
                        {navigationItems.map((item) => {
                            // Fix the active state logic for Home
                            const isActive = item.path === '/' 
                                ? location.pathname === '/'
                                : location.pathname.startsWith(item.path);

                            return (
                                <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                                    <ListItemButton
                                        onClick={() => handleNavigation(item.path)}
                                        selected={isActive}
                                        sx={{
                                            borderRadius: 3,
                                            transition: 'all 0.3s ease',
                                            background: isActive
                                                ? (isDarkMode ? PRIMARY_ORANGE.main : PRIMARY_ORANGE.dark)
                                                : 'transparent',
                                            color: isActive
                                                ? (isDarkMode ? 'white' : theme.textPrimary)
                                                : theme.textPrimary,
                                            boxShadow: isActive
                                                ? (isDarkMode 
                                                    ? '0 2px 8px rgba(254, 106, 46, 0.3)' 
                                                    : '0 2px 8px rgba(21, 101, 192, 0.25)')
                                                : 'none',
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                color: isActive
                                                    ? (isDarkMode ? 'white' : theme.textPrimary)
                                                    : theme.textSecondary,
                                                minWidth: 40,
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.text}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 500,
                                                    fontSize: '0.875rem',
                                                    letterSpacing: '0.025em',
                                                }
                                            }}
                                        />
                                        {item.badge && (
                                            <Chip
                                                label={item.badge}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    background: '#4CAF50',
                                                    color: 'white',
                                                    fontWeight: 700,
                                                    border: 'none',
                                                    '& .MuiChip-label': {
                                                        px: 1,
                                                    }
                                                }}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}

                        {visaSections.map((section) => (
                            <Box key={section.key} sx={{ mb: 1, width: '100%' }}>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleSectionToggle(section.key)}
                                        selected={expandedSections[section.key]}
                                        sx={{
                                            borderRadius: 3,
                                            transition: 'all 0.2s ease',
                                            background: expandedSections[section.key] ? (isDarkMode ? PRIMARY_ORANGE.main : PRIMARY_ORANGE.dark) : 'transparent',
                                            color: expandedSections[section.key] ? (isDarkMode ? 'white' : theme.textPrimary) : theme.textPrimary,
                                            boxShadow: expandedSections[section.key] ? (isDarkMode ? '0 2px 8px rgba(254, 106, 46, 0.3)' : '0 2px 8px rgba(21, 101, 192, 0.25)') : 'none',
                                            '&:hover': {
                                                background: expandedSections[section.key] ? (isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(254, 106, 46, 0.08)') : (isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(254, 106, 46, 0.08)'),
                                                transform: 'translateX(4px)',
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                color: expandedSections[section.key] ? (isDarkMode ? 'white' : theme.textPrimary) : theme.textSecondary,
                                                minWidth: 40,
                                            }}
                                        >
                                            {section.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={section.title}
                                            sx={{
                                                '& .MuiListItemText-primary': {
                                                    fontWeight: 500,
                                                    fontSize: '0.875rem',
                                                    letterSpacing: '0.025em',
                                                }
                                            }}
                                        />
                                        <ExpandMoreIcon sx={{ 
                                            color: theme.textSecondary, 
                                            fontSize: '1.2rem', 
                                            ml: 1,
                                            transform: expandedSections[section.key] ? 'rotate(0deg)' : 'rotate(-90deg)',
                                            transition: 'transform 0.2s ease'
                                        }} />
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={expandedSections[section.key]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding sx={{ width: '100%' }}>
                                        {section.items.map((item) => {
                                            const isActive = item.path === '/'
                                                ? location.pathname === '/'
                                                : location.pathname.startsWith(item.path);
                                            return (
                                                <ListItem key={item.text} disablePadding sx={{ pl: 3, mb: 0.25, width: '100%' }}>
                                                    <ListItemButton
                                                        onClick={() => handleNavigation(item.path)}
                                                        selected={isActive}
                                                        sx={{
                                                            borderRadius: 2,
                                                            transition: 'all 0.2s ease',
                                                            py: 0.5,
                                                            px: 2,
                                                            minHeight: 32,
                                                            width: '100%',
                                                            background: isActive
                                                                ? (isDarkMode ? 'rgba(254, 106, 46, 0.2)' : 'rgba(254, 106, 46, 0.1)')
                                                                : 'transparent',
                                                            '&:hover': {
                                                                background: isActive
                                                                    ? (isDarkMode ? 'rgba(254, 106, 46, 0.3)' : 'rgba(254, 106, 46, 0.15)')
                                                                    : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'),
                                                                transform: 'translateX(2px)',
                                                            }
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={item.text}
                                                            sx={{
                                                                flex: 1,
                                                                '& .MuiListItemText-primary': {
                                                                    fontWeight: isActive ? 500 : 300,
                                                                    color: isActive ? theme.primary : theme.textPrimary,
                                                                    fontSize: '0.75rem',
                                                                    letterSpacing: '0.025em',
                                                                }
                                                            }}
                                                        />
                                                        {item.badge && (
                                                            <Chip
                                                                label={item.badge}
                                                                size="small"
                                                                sx={{
                                                                    height: 16,
                                                                    fontSize: '0.55rem',
                                                                    background: item.badge === 'FREE' 
                                                                        ? '#4CAF50' 
                                                                        : item.badge === 'BETA'
                                                                        ? '#FF9800'
                                                                        : '#9E9E9E',
                                                                    color: 'white',
                                                                    fontWeight: 600,
                                                                    border: 'none',
                                                                    flexShrink: 0,
                                                                    '& .MuiChip-label': {
                                                                        px: 0.75,
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Collapse>
                            </Box>
                        ))}
                    </List>

                    {/* Recent Chats Section */}
                    <Box sx={{ mt: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2 }}>
                            <ChatIcon sx={{ color: theme.textSecondary, mr: 1, fontSize: '1.2rem' }} />
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.textSecondary,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Recent Chats
                            </Typography>
                        </Box>

                        {isLoadingChats ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} sx={{ color: theme.textSecondary }} />
                            </Box>
                        ) : recentChats.length > 0 ? (
                            <List sx={{ pt: 0 }}>
                                {recentChats.map((chat) => (
                                    <ListItem key={chat.id} disablePadding sx={{ mb: 0.5 }}>
                                        <ListItemButton
                                            onClick={() => handleChatNavigation(chat)}
                                            sx={{
                                                borderRadius: 2,
                                                transition: 'all 0.2s ease',
                                                py: 1,
                                                px: 2,
                                                '&:hover': {
                                                    background: isDarkMode
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'rgba(0, 0, 0, 0.04)',
                                                    transform: 'translateX(2px)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    color: theme.textSecondary,
                                                    minWidth: 32,
                                                    '& svg': { fontSize: '1.1rem' }
                                                }}
                                            >
                                                {getVisaIcon(chat.visaType)}
                                            </ListItemIcon>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: theme.textPrimary,
                                                        fontSize: '0.85rem',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {chat.title}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip
                                                        label={getVisaDisplayName(chat.visaType)}
                                                        size="small"
                                                        sx={{
                                                            height: 16,
                                                            fontSize: '0.65rem',
                                                            background: chat.visaType === 'global-talent-visa'
                                                                ? 'rgba(59, 130, 246, 0.1)'
                                                                : 'rgba(16, 185, 129, 0.1)',
                                                            color: chat.visaType === 'global-talent-visa'
                                                                ? '#FE6A2E'
                                                                : '#10b981',
                                                            border: 'none',
                                                            '& .MuiChip-label': {
                                                                px: 1,
                                                            }
                                                        }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <AccessTimeIcon sx={{
                                                            fontSize: '0.7rem',
                                                            color: theme.textSecondary,
                                                            mr: 0.5
                                                        }} />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: theme.textSecondary,
                                                                fontSize: '0.65rem',
                                                            }}
                                                        >
                                                            {formatChatTime(chat.updatedAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.textSecondary,
                                    px: 2,
                                    display: 'block',
                                    textAlign: 'center',
                                    fontStyle: 'italic',
                                }}
                            >
                                No recent chats
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Divider before logout */}
                <Divider sx={{ borderColor: theme.borderColor, mx: 2 }} />

                {/* Logout Section */}
                <Box sx={{ p: 3 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleLogout}
                        startIcon={<LogoutIcon />}
                        sx={{
                            borderRadius: 3,
                            fontWeight: 500,
                            py: 1.5,
                            color: isDarkMode ? '#ef4444' : '#dc2626',
                            borderColor: isDarkMode ? '#ef4444' : '#dc2626',
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(220, 38, 38, 0.05)',
                                borderColor: isDarkMode ? '#f87171' : '#b91c1c',
                                color: isDarkMode ? '#f87171' : '#b91c1c',
                            },
                        }}
                    >
                        Logout
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default GlobalDrawer; 